/**
 * Super Admin User Service - Handles user management across institutions
 * For Super Admin to view and manage users across all institutions
 */

import { apiGet } from '@/utils/api';

// ==================== TYPES ====================

export interface InstitutionWithCounts {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  city: string | null;
  status: string;
  createdAt: string;
  staffCount: number;
  professorCount: number;
  studentCount: number;
  parentCount: number;
  totalUsers: number;
}

export interface AcademicYear {
  id: string;
  name: string;
  isCurrent?: boolean;
}

export interface InstitutionUserStats {
  staffCount: number;
  professorCount: number;
  studentCount: number;
  parentCount: number;
  totalCount: number;
  academicYears: AcademicYear[];
}

export interface Campus {
  id: string;
  name: string;
  sigle: string;
  isPrimary?: boolean;
}

export interface StaffUser {
  id: string;
  email: string | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  phoneCountry: string | null;
  role: string;
  status: string;
  staffCode: string | null;
  profileSubmissionStatus: string | null;
  createdAt: string;
  campuses: Campus[];
  academicYear: AcademicYear | null;
}

export interface ProfessorUser {
  id: string;
  email: string | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  phoneCountry: string | null;
  role: string;
  status: string;
  staffCode: string | null;
  isAlsoProfessor: boolean;
  statutProfesseur: string | null;
  specialite: string | null;
  profileSubmissionStatus: string | null;
  createdAt: string;
  campuses: Campus[];
  academicYear: AcademicYear | null;
  grade: { id: string; labelFr: string } | null;
  departementAttache: { id: string; labelFr: string } | null;
}

export interface StudentParent {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  relationship: string;
  isPrimary: boolean;
}

export interface StudentUser {
  id: string;
  userId: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  dateOfBirth: string | null;
  placeOfBirth: string | null;
  gender: string | null;
  nationality: string | null;
  cnie: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  status: string;
  niveauFormation: number | null;
  diplomeInscrit: string | null;
  codeFiliereInscrit: string | null;
  enrollmentDate: string | null;
  createdAt: string;
  user: {
    id: string;
    status: string;
    profileSubmissionStatus: string | null;
  };
  campus: Campus | null;
  academicYear: AcademicYear | null;
  program: { id: string; name: string; code: string } | null;
  parents: StudentParent[];
}

export interface LinkedStudent {
  id: string;
  firstName: string;
  lastName: string;
  studentCode: string;
  relationship: string;
  isPrimary: boolean;
  campus: Campus | null;
  academicYear: AcademicYear | null;
}

export interface ParentUser {
  id: string;
  email: string | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  phoneCountry: string | null;
  status: string;
  createdAt: string;
  linkedStudents: LinkedStudent[];
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface UserFilters {
  academicYearId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// ==================== API FUNCTIONS ====================

/**
 * Get all institutions with user counts (for Super Admin)
 */
export async function getInstitutionsWithUserCounts(): Promise<InstitutionWithCounts[]> {
  return apiGet('/institutions/with-user-counts');
}

/**
 * Get user statistics for a specific institution
 */
export async function getInstitutionUserStats(
  institutionId: string,
  academicYearId?: string
): Promise<InstitutionUserStats> {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);

  const queryString = params.toString();
  return apiGet(`/users/super-admin/institutions/${institutionId}/stats${queryString ? `?${queryString}` : ''}`);
}

/**
 * Get staff users for a specific institution
 */
export async function getInstitutionStaff(
  institutionId: string,
  filters: UserFilters = {}
): Promise<PaginatedResponse<StaffUser>> {
  const params = new URLSearchParams();
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  return apiGet(`/users/super-admin/institutions/${institutionId}/staff${queryString ? `?${queryString}` : ''}`);
}

/**
 * Get professors for a specific institution
 */
export async function getInstitutionProfessors(
  institutionId: string,
  filters: UserFilters = {}
): Promise<PaginatedResponse<ProfessorUser>> {
  const params = new URLSearchParams();
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  return apiGet(`/users/super-admin/institutions/${institutionId}/professors${queryString ? `?${queryString}` : ''}`);
}

/**
 * Get students for a specific institution
 */
export async function getInstitutionStudents(
  institutionId: string,
  filters: UserFilters = {}
): Promise<PaginatedResponse<StudentUser>> {
  const params = new URLSearchParams();
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  return apiGet(`/users/super-admin/institutions/${institutionId}/students${queryString ? `?${queryString}` : ''}`);
}

/**
 * Get parents for a specific institution
 */
export async function getInstitutionParents(
  institutionId: string,
  filters: UserFilters = {}
): Promise<PaginatedResponse<ParentUser>> {
  const params = new URLSearchParams();
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  return apiGet(`/users/super-admin/institutions/${institutionId}/parents${queryString ? `?${queryString}` : ''}`);
}

// ==================== EXPORT COLUMN DEFINITIONS ====================

export interface ExportColumn {
  key: string;
  label: string;
  category: 'basic' | 'identity' | 'academic' | 'contact' | 'professional' | 'status' | 'links' | 'financial' | 'emergency';
  defaultChecked?: boolean;
}

export const STUDENT_EXPORT_COLUMNS: ExportColumn[] = [
  // Basic (pre-checked)
  { key: 'firstName', label: 'Prenom', category: 'basic', defaultChecked: true },
  { key: 'lastName', label: 'Nom', category: 'basic', defaultChecked: true },
  { key: 'phone', label: 'Telephone', category: 'basic', defaultChecked: true },
  { key: 'email', label: 'Email', category: 'basic', defaultChecked: true },
  // Identity
  { key: 'dateOfBirth', label: 'Date de naissance', category: 'identity' },
  { key: 'gender', label: 'Genre', category: 'identity' },
  { key: 'paysOrigine', label: 'Pays d\'origine', category: 'identity' },
  { key: 'cnie', label: 'CNIE', category: 'identity' },
  { key: 'passport', label: 'Passeport', category: 'identity' },
  // Academic
  { key: 'studentCode', label: 'Code etudiant', category: 'academic' },
  { key: 'programName', label: 'Programme', category: 'academic' },
  { key: 'niveauFormation', label: 'Niveau', category: 'academic' },
  { key: 'diplomeInscrit', label: 'Diplome', category: 'academic' },
  { key: 'codeFiliereInscrit', label: 'Code filiere', category: 'academic' },
  { key: 'situation', label: 'Situation', category: 'academic' },
  { key: 'numeroMassarCNE', label: 'N° Massar/CNE', category: 'academic' },
  { key: 'typeFormation', label: 'Type formation', category: 'academic' },
  { key: 'premiereAnneeInscription', label: 'Premiere annee inscription', category: 'academic' },
  // Bac/Diploma
  { key: 'serieBaccalaureat', label: 'Serie bac', category: 'academic' },
  { key: 'anneeObtentionBac', label: 'Annee obtention bac', category: 'academic' },
  { key: 'diplomeAcces', label: 'Diplome d\'acces', category: 'academic' },
  { key: 'specialiteDiplomeAcces', label: 'Specialite diplome acces', category: 'academic' },
  { key: 'mentionDiplomeAcces', label: 'Mention diplome acces', category: 'academic' },
  // Contact
  { key: 'address', label: 'Adresse', category: 'contact' },
  // Status & Aids
  { key: 'resident', label: 'Resident', category: 'status' },
  { key: 'fonctionnaire', label: 'Fonctionnaire', category: 'status' },
  { key: 'pourcentageBourse', label: 'Pourcentage bourse', category: 'status' },
  { key: 'mobilite', label: 'Mobilite', category: 'status' },
  { key: 'enrollmentDate', label: 'Date d\'inscription', category: 'status' },
  // Emergency contact
  { key: 'urgenceNom', label: 'Contact urgence - Nom', category: 'emergency' },
  { key: 'urgenceRelation', label: 'Contact urgence - Relation', category: 'emergency' },
  { key: 'urgenceNumero', label: 'Contact urgence - Telephone', category: 'emergency' },
];

export const STAFF_EXPORT_COLUMNS: ExportColumn[] = [
  // Basic (pre-checked)
  { key: 'firstName', label: 'Prenom', category: 'basic', defaultChecked: true },
  { key: 'lastName', label: 'Nom', category: 'basic', defaultChecked: true },
  { key: 'phone', label: 'Telephone', category: 'basic', defaultChecked: true },
  { key: 'email', label: 'Email', category: 'basic', defaultChecked: true },
  // Identity
  { key: 'cnie', label: 'CNIE', category: 'identity' },
  { key: 'passport', label: 'Passeport', category: 'identity' },
  { key: 'sexe', label: 'Genre', category: 'identity' },
  { key: 'dateNaissance', label: 'Date de naissance', category: 'identity' },
  { key: 'paysOrigine', label: 'Pays d\'origine', category: 'identity' },
  // Professional
  { key: 'staffCode', label: 'Code personnel', category: 'professional' },
  { key: 'role', label: 'Role', category: 'professional' },
  { key: 'fonction', label: 'Fonction', category: 'professional' },
  { key: 'dateRecrutement', label: 'Date de recrutement', category: 'professional' },
  { key: 'diplome', label: 'Diplome', category: 'professional' },
  // Contact
  { key: 'adresse', label: 'Adresse', category: 'contact' },
  // Financial
  { key: 'salaireMensuelNet', label: 'Salaire mensuel net', category: 'financial' },
  { key: 'rib', label: 'RIB', category: 'financial' },
  // Emergency contact
  { key: 'urgenceNom', label: 'Contact urgence - Nom', category: 'emergency' },
  { key: 'urgenceRelation', label: 'Contact urgence - Relation', category: 'emergency' },
  { key: 'urgenceNumero', label: 'Contact urgence - Telephone', category: 'emergency' },
];

export const PROFESSOR_EXPORT_COLUMNS: ExportColumn[] = [
  // Basic (pre-checked)
  { key: 'firstName', label: 'Prenom', category: 'basic', defaultChecked: true },
  { key: 'lastName', label: 'Nom', category: 'basic', defaultChecked: true },
  { key: 'phone', label: 'Telephone', category: 'basic', defaultChecked: true },
  { key: 'email', label: 'Email', category: 'basic', defaultChecked: true },
  // Identity
  { key: 'cnie', label: 'CNIE', category: 'identity' },
  { key: 'passport', label: 'Passeport', category: 'identity' },
  { key: 'sexe', label: 'Genre', category: 'identity' },
  { key: 'dateNaissance', label: 'Date de naissance', category: 'identity' },
  { key: 'paysOrigine', label: 'Pays d\'origine', category: 'identity' },
  // Professional
  { key: 'staffCode', label: 'Code personnel', category: 'professional' },
  { key: 'statutProfesseur', label: 'Statut professeur', category: 'professional' },
  { key: 'specialite', label: 'Specialite', category: 'professional' },
  { key: 'matieresEnseignees', label: 'Matieres enseignees', category: 'professional' },
  { key: 'grade', label: 'Grade', category: 'professional' },
  { key: 'departement', label: 'Departement', category: 'professional' },
  { key: 'dateRecrutement', label: 'Date de recrutement', category: 'professional' },
  { key: 'estOriginaireEtablissementPublic', label: 'Originaire etablissement public', category: 'professional' },
  { key: 'etablissementOrigine', label: 'Etablissement d\'origine', category: 'professional' },
  // Contact
  { key: 'adresse', label: 'Adresse', category: 'contact' },
  // Financial
  { key: 'salaireMensuelNet', label: 'Salaire mensuel net', category: 'financial' },
  { key: 'rib', label: 'RIB', category: 'financial' },
  { key: 'tauxHoraire', label: 'Taux horaire', category: 'financial' },
  // Emergency contact
  { key: 'urgenceNom', label: 'Contact urgence - Nom', category: 'emergency' },
  { key: 'urgenceRelation', label: 'Contact urgence - Relation', category: 'emergency' },
  { key: 'urgenceNumero', label: 'Contact urgence - Telephone', category: 'emergency' },
];

export const PARENT_EXPORT_COLUMNS: ExportColumn[] = [
  // Basic (pre-checked)
  { key: 'firstName', label: 'Prenom', category: 'basic', defaultChecked: true },
  { key: 'lastName', label: 'Nom', category: 'basic', defaultChecked: true },
  { key: 'phone', label: 'Telephone', category: 'basic', defaultChecked: true },
  { key: 'email', label: 'Email', category: 'basic', defaultChecked: true },
  // Links
  { key: 'linkedStudents', label: 'Etudiants lies', category: 'links' },
  // Status
  { key: 'status', label: 'Statut', category: 'status' },
];

// ==================== TENANT-AWARE API FUNCTIONS ====================

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;

/**
 * Get auth headers with a specific tenant ID (for Super Admin cross-tenant access)
 */
function getAuthHeadersWithTenant(tenantId: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId,
  };

  const token = localStorage.getItem('nexedu_access_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Fetch staff profile with specific tenant context (for Super Admin)
 */
export async function getStaffProfileWithTenant(
  staffUserId: string,
  tenantId: string
): Promise<any> {
  const response = await fetch(`${API_BASE}/profile/admin/staff/${staffUserId}`, {
    method: 'GET',
    headers: getAuthHeadersWithTenant(tenantId),
  });

  if (!response.ok) {
    console.error('Failed to fetch staff profile:', response.status);
    return null;
  }

  return response.json();
}

/**
 * Update staff profile with specific tenant context (for Super Admin)
 */
export async function updateStaffProfileWithTenant(
  staffUserId: string,
  tenantId: string,
  profileData: any
): Promise<any> {
  const response = await fetch(`${API_BASE}/profile/admin/staff/${staffUserId}`, {
    method: 'PUT',
    headers: getAuthHeadersWithTenant(tenantId),
    body: JSON.stringify(profileData),
  });

  if (!response.ok) {
    throw new Error('Failed to update staff profile');
  }

  return response.json();
}

/**
 * Reset staff password with specific tenant context (for Super Admin)
 */
export async function resetStaffPasswordWithTenant(
  staffUserId: string,
  tenantId: string,
  sendEmail: boolean
): Promise<{ tempPassword?: string; message: string }> {
  const response = await fetch(`${API_BASE}/users/staff/${staffUserId}/reset-password`, {
    method: 'POST',
    headers: getAuthHeadersWithTenant(tenantId),
    body: JSON.stringify({ sendEmail }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to reset password');
  }

  return response.json();
}

/**
 * Fetch staff role permissions with specific tenant context (for Super Admin)
 */
export async function getStaffRolePermissionsWithTenant(
  staffUserId: string,
  tenantId: string
): Promise<any> {
  const response = await fetch(`${API_BASE}/users/staff/${staffUserId}/role-permissions`, {
    method: 'GET',
    headers: getAuthHeadersWithTenant(tenantId),
  });

  if (!response.ok) {
    console.error('Failed to fetch role permissions:', response.status);
    return null;
  }

  return response.json();
}

/**
 * Update staff role with specific tenant context (for Super Admin)
 */
export async function updateStaffRoleWithTenant(
  staffUserId: string,
  tenantId: string,
  newRole: string
): Promise<any> {
  const response = await fetch(`${API_BASE}/users/staff/${staffUserId}/role`, {
    method: 'PATCH',
    headers: getAuthHeadersWithTenant(tenantId),
    body: JSON.stringify({ role: newRole }),
  });

  if (!response.ok) {
    throw new Error('Failed to update staff role');
  }

  return response.json();
}

/**
 * Upload staff document with specific tenant context (for Super Admin)
 */
export async function uploadStaffDocumentWithTenant(
  staffUserId: string,
  tenantId: string,
  documentType: string,
  file: File
): Promise<any> {
  const token = localStorage.getItem('nexedu_access_token');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);

  const response = await fetch(`${API_BASE}/profile/admin/staff/${staffUserId}/documents`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Failed to upload document');
  }

  return response.json();
}

/**
 * Delete staff document with specific tenant context (for Super Admin)
 */
export async function deleteStaffDocumentWithTenant(
  staffUserId: string,
  tenantId: string,
  documentId: string
): Promise<void> {
  const response = await fetch(`${API_BASE}/profile/admin/staff/${staffUserId}/documents/${documentId}`, {
    method: 'DELETE',
    headers: getAuthHeadersWithTenant(tenantId),
  });

  if (!response.ok) {
    throw new Error('Failed to delete document');
  }
}

/**
 * Fetch active campuses with specific tenant context (for Super Admin)
 */
export async function fetchActiveCampusesWithTenant(
  tenantId: string
): Promise<{ campuses: Array<{ id: string; name: string; city?: string; isMainCampus?: boolean }> }> {
  const response = await fetch(`${API_BASE}/campus/active`, {
    method: 'GET',
    headers: getAuthHeadersWithTenant(tenantId),
  });

  if (!response.ok) {
    return { campuses: [] };
  }

  return response.json();
}

/**
 * Fetch academic years with specific tenant context (for Super Admin)
 */
export async function fetchAcademicYearsWithTenant(
  tenantId: string
): Promise<Array<{ id: string; name: string; isCurrent?: boolean }>> {
  const response = await fetch(`${API_BASE}/academic-years`, {
    method: 'GET',
    headers: getAuthHeadersWithTenant(tenantId),
  });

  if (!response.ok) {
    return [];
  }

  return response.json();
}

/**
 * Fetch countries (nomenclature - no tenant needed)
 */
export async function fetchCountriesForExport(): Promise<Array<{ code: string; nameFr: string }>> {
  const token = localStorage.getItem('nexedu_access_token');
  const response = await fetch(`${API_BASE}/nomenclature/countries`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    return [];
  }

  return response.json();
}
